"use client";

import { useMemo, useRef, useState } from "react";
import type { EChartsType } from "echarts";
import type { EChartsOption } from "echarts";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import {
  CHART_HEIGHT_DESKTOP,
  CHART_HEIGHT_MOBILE,
  ChartContainer,
  RangeSelector,
  useIsMobile,
} from "@/components/shared/rank-history";
import { usePublisherCCUHistory } from "../hooks/usePublisherCCUHistory";

type RangeKey = "7d" | "30d" | "3m" | "all";

const PUBLISHER_CCU_RANGE_OPTIONS = [
  { label: "7d", key: "7d", seconds: 7 * 24 * 60 * 60 },
  { label: "30d", key: "30d", seconds: 30 * 24 * 60 * 60 },
  { label: "3m", key: "3m", seconds: 90 * 24 * 60 * 60 },
  { label: "All", key: "all", seconds: null },
];

const UTC_DATE_LONG = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

const UTC_DATE_SHORT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});

function utcTodayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function subtractDaysUtcIsoDate(days: number) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function escapeHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function escapeHtmlAttr(value: string) {
  // Same escaping, but kept separate for readability when used in attributes.
  return escapeHtml(value);
}

export function PublisherCCUHistory({ clanid }: { clanid: number }) {
  const { elementRef, isVisible } = useIntersectionObserver({
    rootMargin: "200px",
  });

  const defaultRangeKey: RangeKey = "7d";
  const [selectedRangeKey, setSelectedRangeKey] =
    useState<RangeKey>(defaultRangeKey);
  const [bust, setBust] = useState<number | undefined>(undefined);

  const primaryColor = "#a4d007";

  const { from, to } = useMemo(() => {
    if (selectedRangeKey === "all") {
      return { from: null as string | null, to: utcTodayIsoDate() };
    }
    const opt = PUBLISHER_CCU_RANGE_OPTIONS.find(
      (o) => o.key === selectedRangeKey
    );
    const days = opt?.seconds ? Math.round(opt.seconds / (24 * 60 * 60)) : 0;
    return { from: subtractDaysUtcIsoDate(days), to: utcTodayIsoDate() };
  }, [selectedRangeKey]);

  const { history, isLoading, error } = usePublisherCCUHistory({
    clanid,
    from,
    to,
    bust,
    enabled: Boolean(clanid) && isVisible,
  });

  const chartData = useMemo(
    () =>
      history.map(
        (p) =>
          [Date.parse(`${p.day}T00:00:00Z`), p.total_avg_ccu] as [
            number,
            number
          ]
      ),
    [history]
  );

  const isMobile = useIsMobile();
  const chartHeight = isMobile ? CHART_HEIGHT_MOBILE : CHART_HEIGHT_DESKTOP;

  const chartRef = useRef<EChartsType | null>(null);

  const chartOptions = useMemo<EChartsOption>(() => {
    const axisColor = "#8f98a0";
    const gridLeft = isMobile ? 32 : 46;
    const gridRight = isMobile ? 16 : 24;
    const gridBottom = isMobile ? 56 : 70;

    const hexToRgba = (hex: string, alpha: number) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formatter = (params: any) => {
      const param = Array.isArray(params) ? params[0] : params;
      const dataIndex = param?.dataIndex as number | undefined;
      const value = param?.value as [number, number];
      const ts = value?.[0];
      const total = value?.[1] ?? 0;

      const dateLabel =
        typeof ts === "number" ? UTC_DATE_LONG.format(new Date(ts)) : "";

      const items =
        typeof dataIndex === "number"
          ? history?.[dataIndex]?.top_games ?? undefined
          : undefined;

      // Mobile-adaptive styles
      const fontSizeSmall = isMobile ? "10px" : "12px";
      const fontSizeMedium = isMobile ? "11px" : "12px";
      const fontSizeLarge = isMobile ? "12px" : "14px";
      const fontSizeDate = isMobile ? "11px" : "inherit";
      const gapSmall = isMobile ? "6px" : "12px";
      const gapMedium = isMobile ? "4px" : "6px";
      const marginSmall = isMobile ? "4px" : "8px";
      const marginMedium = isMobile ? "6px" : "10px";
      const paddingTop = isMobile ? "6px" : "8px";
      const minWidth = isMobile ? "240px" : "360px";
      const maxWidth = isMobile ? "280px" : "420px";
      const maxNameWidth = isMobile ? "140px" : "260px";
      const topGamesCount = 5;
      const rankWidth = isMobile ? "16px" : "18px";
      const rankGap = isMobile ? "6px" : "8px";

      const totalRow = `
        <div style="display:flex;align-items:baseline;justify-content:space-between;gap:${gapSmall};">
          <div style="color:#8f98a0;font-size:${fontSizeSmall};letter-spacing:.2px;">Total Players</div>
          <div style="color:#c7d5e0;font-weight:700;font-size:${fontSizeLarge};font-variant-numeric:tabular-nums;">
            ${Number(total).toLocaleString("en-US")}
          </div>
        </div>
      `;

      const topHeader = `
        <div style="margin-top:${marginMedium};padding-top:${paddingTop};border-top:1px solid rgba(58,74,90,.65);
          color:#8f98a0;font-size:${fontSizeSmall};letter-spacing:.2px;">
          Top ${topGamesCount} Games
        </div>
      `;

      const topBody =
        items && items.length
          ? `
            <div style="margin-top:${marginSmall};display:flex;flex-direction:column;gap:${gapMedium};">
              ${items
                .slice(0, topGamesCount)
                .map((it) => {
                  const name = escapeHtml(it.name ?? "");
                  const href = `/app/${Number(it.appid)}`;
                  const hrefSafe = escapeHtmlAttr(href);
                  const avg = Number(it.avg_online ?? 0).toLocaleString(
                    "en-US"
                  );
                  const rank = Number(it.rank ?? 0);
                  return `
                    <div style="display:flex;align-items:center;justify-content:space-between;gap:${gapSmall};">
                      <div style="display:flex;align-items:center;gap:${rankGap};min-width:0;">
                        <div style="width:${rankWidth};color:#8f98a0;font-size:${fontSizeSmall};font-variant-numeric:tabular-nums;text-align:right;">
                          ${rank ? `#${rank}` : ""}
                        </div>
                        <a
                          href="${hrefSafe}"
                          style="color:#c7d5e0;text-decoration:none;max-width:${maxNameWidth};overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:${fontSizeMedium};"
                        >${name}</a>
                      </div>
                      <div style="color:#c7d5e0;font-size:${fontSizeMedium};font-variant-numeric:tabular-nums;">
                        ${avg}
                      </div>
                    </div>
                  `;
                })
                .join("")}
            </div>
          `
          : `<div style="margin-top:${marginSmall};color:#8f98a0;font-size:${fontSizeSmall};">No data</div>`;

      return `
        <div style="min-width:${minWidth};max-width:${maxWidth};">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:${gapSmall};margin-bottom:${marginSmall};">
            <div style="font-weight:700;color:#c7d5e0;font-size:${fontSizeDate};line-height:1.2;">${dateLabel}</div>
          </div>
          ${totalRow}
          ${topHeader}
          ${topBody}
        </div>
      `;
    };

    return {
      backgroundColor: "transparent",
      animation: false,
      tooltip: {
        trigger: "axis",
        backgroundColor: "#11161d",
        borderColor: "#3a4a5a",
        borderWidth: 1,
        confine: true,
        padding: isMobile ? [6, 8] : [8, 12],
        textStyle: { color: "#cfd8e3", fontSize: isMobile ? 10 : 12 },
        axisPointer: {
          type: "line",
          lineStyle: { color: primaryColor, width: 1 },
        },
        formatter,
        ...(isMobile && {
          position: (
            point: number[],
            _params: unknown,
            _el: unknown,
            _rect: unknown,
            size: { contentSize: [number, number]; viewSize: [number, number] }
          ) => {
            const [contentW, contentH] = size.contentSize;
            const [viewW, viewH] = size.viewSize;

            const padding = 10;
            const offsetX = 12;
            const offsetY = 28;

            const clamp = (v: number, min: number, max: number) =>
              Math.max(min, Math.min(max, v));

            // Якщо tooltip ширший за доступний простір — просто центруємо.
            const maxX = viewW - contentW - padding;
            const maxY = viewH - contentH - padding;

            let x: number;
            if (contentW >= viewW - padding * 2) {
              x = (viewW - contentW) / 2;
            } else {
              const pointX = point?.[0] ?? viewW / 2;
              const preferRight = pointX < viewW / 2;
              x = preferRight ? pointX + offsetX : pointX - contentW - offsetX;
              x = clamp(x, padding, maxX);
            }

            // На мобільних дефолтно ставимо tooltip нижче точки (щоб не "ліз" вгору),
            // а якщо знизу не влазить — перекидаємо над точку, але тримаємо в межах.
            const pointY = point?.[1] ?? 0;
            const minY = padding + 16; // трохи нижче верхнього краю
            const yPreferBottom = pointY + offsetY;
            const yPreferTop = pointY - contentH - offsetY;
            const yCandidate =
              yPreferBottom <= maxY ? yPreferBottom : yPreferTop;
            const y = clamp(yCandidate, minY, maxY);

            return [x, y];
          },
        }),
      },
      grid: {
        left: gridLeft,
        right: gridRight,
        top: 24,
        bottom: gridBottom,
        containLabel: true,
      },
      xAxis: {
        type: "time",
        boundaryGap: [0, 0],
        axisLabel: {
          color: axisColor,
          formatter: (value: number) => {
            return UTC_DATE_SHORT.format(new Date(value));
          },
        },
        axisLine: { lineStyle: { color: "#2a475e" } },
        axisTick: { lineStyle: { color: "#2a475e" } },
        splitLine: {
          show: true,
          lineStyle: { color: "#1b2838", opacity: 0.5 },
        },
      },
      yAxis: {
        type: "value",
        scale: true,
        axisLabel: {
          color: axisColor,
          formatter: (v: number) => {
            if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
            if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
            return String(v);
          },
        },
        splitLine: {
          show: true,
          lineStyle: { color: "#2a3a4a", opacity: 0.4 },
        },
        axisLine: { lineStyle: { color: "#2a475e" } },
        axisTick: { lineStyle: { color: "#2a475e" } },
      },
      dataZoom: [
        { type: "inside", throttle: 70 },
        {
          type: "slider",
          height: 26,
          bottom: 26,
          backgroundColor: "#11161d",
          borderColor: "#0e141b",
          dataBackground: {
            lineStyle: { color: "#2f3d4a" },
            areaStyle: { color: "#11161d" },
          },
          fillerColor: hexToRgba(primaryColor, 0.25),
          handleStyle: {
            color: primaryColor,
            borderColor: primaryColor,
            borderWidth: 1,
          },
          textStyle: { color: axisColor, fontSize: 11 },
        },
      ],
      series: [
        {
          name: "Total Players",
          type: "line",
          smooth: 1.0,
          symbol: "none",
          showSymbol: false,
          connectNulls: true,
          lineStyle: { width: 1.5, color: primaryColor },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: hexToRgba(primaryColor, 0.25) },
                { offset: 1, color: hexToRgba(primaryColor, 0.05) },
              ],
            },
          },
          emphasis: { focus: "series" },
          data: chartData,
        },
      ],
    };
  }, [chartData, isMobile, primaryColor, history]);

  const handleRangeChange = (key: string) => {
    setSelectedRangeKey(key as RangeKey);
  };

  const handleRefresh = () => {
    setBust(Date.now());
  };

  return (
    <div
      ref={elementRef}
      className="bg-[#16202d] rounded-lg border border-[#2a475e] p-3 md:p-6 space-y-3 md:space-y-4"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div>
          <h3 className="text-base md:text-lg font-semibold text-white">
            Total Players History
          </h3>
          <p className="text-[#8f98a0] text-xs md:text-sm">
            Sum of the daily average number of players across all games from
            this company.
          </p>
        </div>

        <RangeSelector
            chart="publisher_ccu"
          selectedRangeKey={selectedRangeKey}
          onRangeChange={handleRangeChange}
          onRefresh={handleRefresh}
          isLoading={isLoading}
          isMobile={isMobile}
          primaryColor={primaryColor}
          rangeOptions={PUBLISHER_CCU_RANGE_OPTIONS}
        />
      </div>

      <ChartContainer
            chart="publisher_ccu"
        chartOptions={chartOptions}
        isLoading={isLoading}
        error={error}
        hasData={history.length > 0}
        height={chartHeight}
        primaryColor={primaryColor}
        onChartReady={(chart) => {
          chartRef.current = chart;
        }}
      />
    </div>
  );
}
